import AppContext from 'App/AppContext';
import {
  IPDefaultButton,
  IPIconButton,
  IPModal,
  IPPivot,
  IPPivotItem,
  IPPrimaryButton,
  IPSeparator,
  IPText,
} from 'baseComponents';
import { IPStack } from 'baseComponents/Stack/IPStack';
import ActivityTrail from 'components/Activity/ActivityTrail';
import AuditTrail from 'components/Audit/AuditTrail';
import DialogConfirmDelete from 'components/Dialogs/DialogConfirmDelete';
import DialogYesNo from 'components/Dialogs/DialogYesNo';
import OverlayLoader from 'components/Loading/OverlayLoader';
import { ErrorMessage } from 'components/Notification/ErrorMessage';
import TaskTypeForm from 'components/Tasks/Forms/TaskTypeForm';
import { ISingleTaskTypeProps } from 'components/Tasks/SingleTask';
import { canUpdateTaskField, TaskFieldTypes } from 'components/Tasks/TaskAuthHelper';
import TaskTopTasksBar from 'components/Tasks/TaskTopTasksBar';
import { getTaskViewModalStackStyles, getTaskViewModalStyles } from 'components/Tasks/View/TaskRenderHelpers';
import {
  cancelIcon,
  globalStackStylesHeight100,
  globalStackTokensGapMedium,
  globalStackTokensGapSmall,
} from 'globalStyles';
import Joi from 'joi';
import { ApprovalState } from 'models/approval';
import Task from 'models/tasks/task';
import TaskType from 'models/tasks/taskType';
import { Fragment, useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { TaskDetailOwner } from '../Details/TaskDetailOwner';
import { ApprovalTaskDetail } from './ApprovalTaskDetail';

interface IApprovalTaskProps extends ISingleTaskTypeProps {
  onSaveAsNewTask: () => void;
  onChangeCompletionDate: (completionDate: Date) => void;
}

const ApprovalTask = (props: IApprovalTaskProps) => {
  const { t } = useTranslation(['task', 'control', 'translation', 'dateTimeComponent']);
  const appContext = useContext(AppContext);
  const [err, setErr] = useState<Joi.ValidationError | undefined>(undefined);
  const [selectedPivot, setSelectedPivot] = useState<string>(props.selectedPivot ?? 'Checklist');
  const [showRemoveDialog, setShowRemoveDialog] = useState<boolean>(false);
  const [showCancelDialog, setShowCancelDialog] = useState<boolean>(false);

  useEffect(() => {
    props.onBeforeSaveCallback(onBeforeSave);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const _localizedFields = {
      assignment: t('task:Assignment.Label'),
      startDateTime: t('task:Start.Label'),
      endDateTime: t('task:End.ErrorMessage'),
      name: t('task:Name.Label'),
      deadline: t('task:Deadline.ErrorMessage'),
    };

    const result = props.task.validate(_localizedFields);
    setErr(result.error);
  }, [t, props.task]);

  useEffect(() => {
    if (appContext.isMobileView) {
      setSelectedPivot('TaskDetails');
    } else if (props.task.taskTypeId) {
      setSelectedPivot('TaskType');
    } else {
      setSelectedPivot('Checklist');
    }
  }, [appContext.isMobileView, props.task.taskTypeId]);

  useEffect(() => {
    //Single task requests to show a specific pivot
    if (props.selectedPivot) {
      setSelectedPivot(props.selectedPivot);
    }
  }, [props.selectedPivot]);

  const onBeforeSave = async (task: Task): Promise<Task | undefined> => {
    //when the task is approved or rejected, set the task state to complete before saving
    if (!task.isCompleted() && isApprovedOrRejected(task)) {
      task.setComplete();
    }

    return task;
  };

  const validate = (fieldName: string): string | undefined => {
    if (err) {
      const fieldError = err.details.find((e) => {
        return e.context?.key === fieldName || e.message.toLowerCase().includes(fieldName.toLowerCase());
      });

      return fieldError ? fieldError.message : undefined;
    }

    return undefined;
  };

  const hasErrors = (): boolean => {
    return err ? true : false;
  };

  const cannotSaveTask = (): boolean => {
    return (props.task.taskId !== -1 && props.task.isEqual(props.orgTask)) || hasErrors();
  };

  const isApprovedOrRejected = (task: Task): boolean => {
    return task.approved !== ApprovalState.Pending;
  };

  const getIsOutlookCreateDisabled = (): boolean => {
    return (
      hasErrors() ||
      props.isActionPending ||
      !props.task.isAssigned() ||
      (props.disallowSchedule ?? false) ||
      isApprovedOrRejected(props.task) ||
      !canUpdateTaskField(props.task, props.canUpdate, TaskFieldTypes.OutlookCreate, appContext)
    );
  };

  const onCancel = () => {
    if (cannotSaveTask()) {
      props.onCancel();
    } else {
      setShowCancelDialog(true);
    }
  };

  const getTaskDetails = () => {
    return (
      <ApprovalTaskDetail
        task={props.task}
        orgTask={props.orgTask}
        onUpdate={(newTask: Task) => {
          props.onUpdateTask(newTask);
        }}
        getErrorMessage={validate}
        isActionPending={props.isActionPending}
        tags={props.tags}
        addTagToTaskState={props.addTagToTaskState}
        removeTagFromTaskState={props.removeTagFromTaskState}
        onChangeCompletionDate={props.onChangeCompletionDate}
        windowLevel={props.windowLevel}
        navigateExternal={props.navigateExternal}
        canUpdate={props.canUpdate}
      />
    );
  };

  const onClickArchive = () => {
    const newTask = props.task.clone();
    newTask.setArchived();
    props.onUpdateTask(newTask);
    props.onSave(newTask, false);
  };

  //
  // Render helpers
  //
  const getOutlookButton = (): JSX.Element | null => {
    if (!props.task.eventId && !props.task.isSeries()) {
      //create
      return (
        <IPStack>
          <IPStack.Item>
            <IPPrimaryButton
              className="isoplanner-task-scheduleinoutlook"
              disabled={getIsOutlookCreateDisabled()}
              onClick={() => {
                props.createInOutlook();
              }}
            >
              {t('task:Outlook.CreateOutlook')}
            </IPPrimaryButton>
          </IPStack.Item>
        </IPStack>
      );
    } else if (props.task.eventId && !props.task.isSeries()) {
      //remove
      return (
        <IPStack>
          <IPStack.Item>
            <IPPrimaryButton
              text={t('task:Outlook.RemoveOutlook')}
              disabled={
                hasErrors() ||
                props.isActionPending ||
                !canUpdateTaskField(props.task, props.canUpdate, TaskFieldTypes.OutlookRemove, appContext)
              }
              onClick={() => {
                props.removeFromOutlook();
              }}
            />
          </IPStack.Item>
        </IPStack>
      );
    }

    return null;
  };

  const footer = (
    <IPStack horizontal={!appContext.isMobileView} verticalAlign="end" tokens={globalStackTokensGapSmall}>
      <IPStack.Item>
        <IPStack horizontal tokens={globalStackTokensGapSmall}>
          <IPPrimaryButton
            disabled={props.isActionPending || cannotSaveTask()} //disable save as template
            onClick={async () => {
              props.onSave(props.task, false);
            }}
          >
            {t('translation:General.Button.Save')}
          </IPPrimaryButton>
          <IPDefaultButton
            onClick={() => {
              onCancel();
            }}
          >
            {t('translation:General.Button.Cancel')}
          </IPDefaultButton>
        </IPStack>
      </IPStack.Item>
      <IPStack.Item grow>
        <IPStack horizontal horizontalAlign={appContext.isMobileView ? undefined : 'space-around'}>
          {getOutlookButton()}
        </IPStack>
      </IPStack.Item>
    </IPStack>
  );

  //
  // Main render
  //
  return (
    <IPModal forceFocusInsideTrap={false} isOpen={props.isOpen} styles={getTaskViewModalStyles(props.windowLevel)}>
      {props.isLoading && <OverlayLoader text={t('task:Progress.Save')} />}
      <IPStack
        verticalFill
        tokens={globalStackTokensGapSmall}
        styles={getTaskViewModalStackStyles(appContext.isMobileView, false)}
      >
        <IPStack.Item>
          <IPStack horizontal verticalAlign="center" horizontalAlign="space-between">
            <IPStack.Item>
              <IPStack horizontal={!appContext.isMobileView} verticalAlign="center" tokens={globalStackTokensGapMedium}>
                <IPStack.Item>
                  <IPText variant="xxLarge">{t('task:Title.Approval.Edit')}</IPText>
                </IPStack.Item>
              </IPStack>
            </IPStack.Item>
            {!props.compactView && (
              <IPStack.Item>
                <TaskTopTasksBar
                  task={props.task}
                  onClickArchive={onClickArchive}
                  navigateExternal={props.navigateExternal}
                  canUpdate={props.canUpdate}
                  canDelete={props.canDelete}
                  orgTask={props.orgTask}
                  canSave={!hasErrors()}
                  isLoading={props.isLoading}
                  isActionPending={props.isActionPending}
                />
              </IPStack.Item>
            )}
            <IPStack.Item>
              <IPIconButton iconProps={cancelIcon} onClick={onCancel} />
            </IPStack.Item>
          </IPStack>
          <IPStack.Item>
            <TaskDetailOwner
              orgTask={props.orgTask}
              task={props.task}
              onUpdate={props.onUpdateTask}
              canUpdate={props.canUpdate}
            />
          </IPStack.Item>
          <ErrorMessage error={props.error} customAction={props.onAfterErrorAction} />
          <IPSeparator />
        </IPStack.Item>
        {props.compactView && (
          <IPStack.Item>
            <TaskTopTasksBar
              task={props.task}
              onClickArchive={onClickArchive}
              navigateExternal={props.navigateExternal}
              canUpdate={props.canUpdate}
              canDelete={props.canDelete}
              orgTask={props.orgTask}
              canSave={!hasErrors()}
              compact={true}
              isLoading={props.isLoading}
              isActionPending={props.isActionPending}
            />
          </IPStack.Item>
        )}
        <IPStack.Item grow>
          <IPStack horizontal styles={globalStackStylesHeight100} tokens={globalStackTokensGapSmall}>
            {!appContext.isMobileView && (
              <Fragment>
                <IPStack.Item styles={{ root: { width: '50%' } }}>{getTaskDetails()}</IPStack.Item>
                <IPSeparator vertical></IPSeparator>
              </Fragment>
            )}
            <IPStack.Item styles={!appContext.isMobileView ? { root: { width: '50%' } } : { root: { width: '100%' } }}>
              <IPStack verticalFill tokens={globalStackTokensGapSmall}>
                <IPStack.Item>
                  <IPPivot
                    selectedKey={selectedPivot}
                    onLinkClick={(item) => setSelectedPivot(item?.props.itemKey ?? '')}
                    overflowBehavior="menu"
                  >
                    {appContext.isMobileView && (
                      <IPPivotItem
                        className="isoplanner-task-pivot-details"
                        key="TaskDetails"
                        itemKey="TaskDetails"
                        headerText={t('task:Pivot.TaskDetails')}
                      />
                    )}
                    {props.hasFormFeature && props.task.taskTypeId && props.task.taskTypeId > 0 && (
                      <IPPivotItem
                        className="isoplanner-task-pivot-tasktype"
                        key="TaskType"
                        itemKey="TaskType"
                        headerText={TaskType.tabNameForTask(props.task.taskTypeId, props.task.formVersion, appContext)}
                      />
                    )}
                    {props.task.commentTrailId > 0 && (
                      <IPPivotItem
                        className="isoplanner-task-pivot-activity"
                        key="Activities"
                        itemKey="Activities"
                        headerText={t('task:Pivot.Activities')}
                      />
                    )}
                    {props.task.auditTrailId > 0 && (
                      <IPPivotItem
                        className="isoplanner-task-pivot-changelog"
                        key="Audit"
                        itemKey="Audit"
                        headerText={t('task:Pivot.Audit')}
                      />
                    )}
                  </IPPivot>
                </IPStack.Item>
                {selectedPivot === 'TaskDetails' && (
                  <Fragment>
                    <IPStack.Item grow style={{ height: appContext.isMobileView ? '40vh' : 0 }}>
                      {getTaskDetails()}
                    </IPStack.Item>
                  </Fragment>
                )}
                <TaskTypeForm
                  isOpen={selectedPivot === 'TaskType'}
                  task={props.task}
                  links={props.links}
                  compact={true}
                  readonly={props.orgTask.isCompleted()} //the SAVED state decides: picking another status must not unlock the form
                  onUpdateData={props.onUpdateKPIData}
                  onSetData={props.onSetKPIData}
                  onUpdateTaskForForm={props.onUpdateTaskForForm}
                  addLinks={props.addLinkToTaskState}
                  setLinks={props.setLinkToTaskState}
                  removeLink={props.removeLinkFromTaskState}
                  showErrors={props.showFormErrors}
                  onValidate={props.onValidateForm}
                ></TaskTypeForm>
                {selectedPivot === 'Activities' && (
                  <IPStack.Item grow>
                    <ActivityTrail
                      auditTrailId={props.masterTask?.auditTrailId ?? 0}
                      commentTrailId={props.masterTask?.commentTrailId ?? 0}
                      taskId={props.masterTask?.taskId ?? 0}
                      authSchemaIds={[props.task.authSchemaId]}
                      onError={props.onError}
                    />
                  </IPStack.Item>
                )}
                {selectedPivot === 'Audit' && (
                  <IPStack.Item grow>
                    <AuditTrail auditTrailId={props.task.auditTrailId} />
                  </IPStack.Item>
                )}
              </IPStack>
            </IPStack.Item>
          </IPStack>
        </IPStack.Item>
        <IPStack.Item>
          <IPSeparator styles={{ root: { height: 1 } }} />
        </IPStack.Item>
        <IPStack.Item>{footer}</IPStack.Item>
      </IPStack>
      <DialogConfirmDelete
        onNo={() => {
          setShowRemoveDialog(false);
        }}
        onYes={() => {
          setShowRemoveDialog(false);
          props.onRemove();
        }}
        title={props.task.isSeries() ? t('task:Template.RemoveDialogTitle') : t('task:Remove.DialogTitle')}
        subText={props.task.isSeries() ? t('task:Template.RemoveDialogSubText') : t('task:Remove.DialogSubTitle')}
        hidden={!showRemoveDialog}
        confirmText={!props.task.isSeries() ? t('task:Remove.RemoveDialogConfirmText') : undefined}
      />
      <DialogYesNo
        onNo={() => {
          setShowCancelDialog(false);
        }}
        onYes={() => {
          setShowCancelDialog(false);

          props.onCancel();
        }}
        title={t('task:Cancel.DialogTitle')}
        subText={t('task:Cancel.DialogSubTitle')}
        hidden={!showCancelDialog}
      />
    </IPModal>
  );
};

export default ApprovalTask;
